import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'boss_auth_token';
const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

// Helper: get stored token
const getToken = () => localStorage.getItem(TOKEN_KEY);
const setToken = (t) => { if (t) localStorage.setItem(TOKEN_KEY, t); };
const clearToken = () => localStorage.removeItem(TOKEN_KEY);

// Helper: build auth headers
export const authHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // The callback component will exchange the code/session_id and establish the session first.
    if (
      window.location.hash?.includes('session_id=') ||
      (window.location.pathname === '/auth/google' && window.location.search?.includes('code='))
    ) {
      setLoading(false);
      return;
    }
    const token = getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setUser(await res.json());
      } else {
        clearToken();
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      // Structured error: backend returns detail = { code, message } for google-account-no-password.
      // Also fall back to status-code detection if the body failed to parse (defensive).
      const detailCode = err.detail && typeof err.detail === 'object' ? err.detail.code : null;
      if (detailCode === 'google_account_no_password' || res.status === 409) {
        const e = new Error((err.detail && err.detail.message) || 'This email already has a Google account. Sign in with Google, or reset your password.');
        e.code = 'google_account_no_password';
        throw e;
      }
      const msg = typeof err.detail === 'string' ? err.detail : Array.isArray(err.detail) ? err.detail.map(e => e.msg || e).join(', ') : 'Login failed';
      throw new Error(msg);
    }
    const data = await res.json();
    if (data.token) setToken(data.token);
    setUser(data);
    return data;
  };

  const register = async (name, email, password) => {
    const res = await fetch(`${API}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const msg = typeof err.detail === 'string' ? err.detail : Array.isArray(err.detail) ? err.detail.map(e => e.msg || e).join(', ') : 'Registration failed';
      throw new Error(msg);
    }
    const data = await res.json();
    if (data.token) setToken(data.token);
    setUser(data);
    return data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + '/auth/google';
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    // CSRF protection: random state, verified on return
    const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('google_oauth_state', state);
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'online',
      prompt: 'select_account',
      state,
    });
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;

    // Open a centered popup — users can close it with the native X any time
    const w = 480;
    const h = 620;
    const y = window.top.outerHeight / 2 + window.top.screenY - h / 2;
    const x = window.top.outerWidth / 2 + window.top.screenX - w / 2;
    const popup = window.open(
      authUrl,
      'google_oauth',
      `width=${w},height=${h},left=${Math.max(0, x)},top=${Math.max(0, y)},popup=yes,scrollbars=yes,resizable=yes`
    );

    // Fallback: if popup was blocked, do the classic full-page redirect
    if (!popup) {
      window.location.href = authUrl;
      return;
    }

    // Listen for the success message from the popup (same-origin postMessage)
    const onMessage = (ev) => {
      if (ev.origin !== window.location.origin) return;
      if (ev.data?.type === 'google-auth-success' && ev.data.token) {
        setToken(ev.data.token);
        setUser(ev.data.user);
        cleanup();
      } else if (ev.data?.type === 'google-auth-error') {
        cleanup();
      }
    };
    // Detect manual close (user clicked the browser X)
    const poll = setInterval(() => {
      if (popup.closed) cleanup();
    }, 500);
    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      clearInterval(poll);
      try { if (!popup.closed) popup.close(); } catch { /* cross-origin */ }
    };
    window.addEventListener('message', onMessage);
  };

  const logout = async () => {
    const token = getToken();
    await fetch(`${API}/auth/logout`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).catch(() => {});
    clearToken();
    setUser(null);
  };

  const refreshUser = async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setUser(await res.json());
    } catch { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
